import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateTime } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import { useFormSubmissionDetailQuery } from "../api/queries";
import { FormSubmissionFileAnswer } from "../ui/FormSubmissionFileAnswer";
import { getFormSubmissionFilePublicUrl } from "../utils/form-submission-file-url";
import { formatFormSubmissionFieldValue } from "../utils/format-form-submission-field-value";

const statusLabels: Record<string, string> = {
  draft: "ຮ່າງ",
  submitted: "ສົ່ງແລ້ວ",
  in_review: "ກຳລັງກວດ",
  approved: "ອະນຸມັດ",
  rejected: "ປະຕິເສດ",
  cancelled: "ຍົກເລີກ",
};

const statusVariant: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  draft: "secondary",
  submitted: "default",
  in_review: "secondary",
  approved: "default",
  rejected: "destructive",
  cancelled: "outline",
};

export function FormSubmissionDetailPage() {
  const nav = useNavigate();
  const { id: formId, submissionId } = useParams({
    from: "/app/forms/$id/submissions/$submissionId",
  });

  const detail = useFormSubmissionDetailQuery(formId ?? "", submissionId ?? "");

  return (
    <>
      <Header />
      <Main>
        <QueryState
          result={detail}
          variant="fullscreen"
          isEmpty={!detail.data}
          emptyFallback={
            <div className="text-center">
              <p className="text-muted-foreground">ບໍ່ພົບການສົ່ງນີ້</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() =>
                  nav({
                    to: "/app/forms/$id/submissions",
                    params: { id: formId ?? "" },
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined as
                        | Array<{ field: string; dir: "asc" | "desc" }>
                        | undefined,
                      filters: undefined as
                        | Array<{
                            field: string;
                            op: string;
                            value: unknown;
                          }>
                        | undefined,
                    },
                  })
                }
              >
                ກັບລາຍການສົ່ງ
              </Button>
            </div>
          }
        >
          {detail.data && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="">
                  <h1 className="font-bold text-2xl tracking-tight">
                    ລາຍລະອຽດການສົ່ງຟອມ
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    {detail.data.submission.formName} · ເວີຊັນ{" "}
                    {detail.data.submission.versionLabel}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      to: "/app/forms/$id/submissions",
                      params: { id: formId ?? "" },
                      search: {
                        offset: 0,
                        limit: 20,
                        sort: undefined as
                          | Array<{ field: string; dir: "asc" | "desc" }>
                          | undefined,
                        filters: undefined as
                          | Array<{
                              field: string;
                              op: string;
                              value: unknown;
                            }>
                          | undefined,
                      },
                    })
                  }
                  className="mb-2 -ml-2"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  ກັບລາຍການສົ່ງ
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">ຂໍ້ມູນທົ່ວໄປ</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">ສະຖານະ</span>
                    <div className="mt-1">
                      <Badge
                        variant={
                          statusVariant[detail.data.submission.status] ??
                          "secondary"
                        }
                      >
                        {statusLabels[detail.data.submission.status] ??
                          detail.data.submission.status}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ຜູ້ສົ່ງ</span>
                    <div className="mt-1 font-medium">
                      {detail.data.submission.submitter.name}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {detail.data.submission.submitter.email}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ເວລາສົ່ງ</span>
                    <div className="mt-1">
                      {detail.data.submission.submittedAt
                        ? formatDateTime(detail.data.submission.submittedAt)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ເລີ່ມກອກ</span>
                    <div className="mt-1">
                      {formatDateTime(detail.data.submission.createdAt)}
                    </div>
                  </div>
                  {detail.data.submission.reviewer && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">ຜູ້ກວດ</span>
                      <div className="mt-1 font-medium">
                        {detail.data.submission.reviewer.name}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {detail.data.submission.reviewer.email}
                      </div>
                      {detail.data.submission.reviewedAt && (
                        <div className="mt-1 text-xs">
                          {formatDateTime(detail.data.submission.reviewedAt)}
                        </div>
                      )}
                    </div>
                  )}
                  {detail.data.submission.notes && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">
                        ຫມາຍເຫດ (ຜູ້ສົ່ງ)
                      </span>
                      <div className="mt-1 whitespace-pre-wrap">
                        {detail.data.submission.notes}
                      </div>
                    </div>
                  )}
                  {detail.data.submission.reviewNotes && (
                    <div className="sm:col-span-2">
                      <span className="text-muted-foreground">
                        ຫມາຍເຫດການກວດ
                      </span>
                      <div className="mt-1 whitespace-pre-wrap">
                        {detail.data.submission.reviewNotes}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">ຄຳຕອບໃນຟອມ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {detail.data.fields.map((f) => (
                    <div
                      key={f.fieldId}
                      className="border-b pb-4 last:border-0 last:pb-0"
                    >
                      <div className="font-medium">{f.label}</div>
                      {f.hint && (
                        <div className="text-muted-foreground text-xs">
                          {f.hint}
                        </div>
                      )}
                      {f.fieldType === "file" ? (
                        <FormSubmissionFileAnswer
                          fieldType={f.fieldType}
                          value={f.value}
                          fileKey={f.fileKey}
                        />
                      ) : (
                        <pre className="wrap-break-word mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-sans text-sm">
                          {formatFormSubmissionFieldValue(
                            f.fieldType,
                            f.value,
                            f.fileKey,
                          )}
                        </pre>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {detail.data.attachments.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">ໄຟລ໌ແນບເພີ່ມ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="list-inside list-disc space-y-2 text-sm">
                      {detail.data.attachments.map((a) => (
                        <li key={a.id}>
                          <a
                            href={getFormSubmissionFilePublicUrl(a.fileKey)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2"
                          >
                            {a.fileName}
                            <ExternalLink
                              className="h-3.5 w-3.5 shrink-0"
                              aria-hidden
                            />
                          </a>
                          {a.description && (
                            <span className="text-muted-foreground">
                              {" "}
                              — {a.description}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
